import React, { useEffect, useState, useContext } from 'react';
import {
  Container,
  Typography,
  Card,
  Tooltip,
  CardContent,
  Stack,
  Button,
  IconButton,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Alert
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Visibility, Edit, Delete } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';

import contribuenteApi from '../../api/contribuenteApi';
import documentoApi from '../../api/documentoApi';
import scadenzaApi from '../../api/scadenzaApi';

import ConfirmDialog from '../../components/ConfirmDeleteModal';
import ProtectedAction from '../../components/ProtectedAction';
import { AuthContext } from '../../auth/AuthContext';
import { formatDate } from '../../utils/dateFormatter';
import BackButtonDesktop from '../../components/BackButtonDesktop';

export default function ContribuenteDetailPageMobile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [contribuente, setContribuente] = useState(null);
  const [docs, setDocs] = useState([]);
  const [docsPage, setDocsPage] = useState(0);
  const [docsTotalPages, setDocsTotalPages] = useState(0);
  const [scadenze, setScadenze] = useState([]);
  const [scadPage, setScadPage] = useState(0);
  const [scadTotalPages, setScadTotalPages] = useState(0);

  const [selectedDocumentIds, setSelectedDocumentIds] = useState([]);
  const [mergeError, setMergeError] = useState(null);
  const [merging, setMerging] = useState(false);

  const [openDelete, setOpenDelete] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [deleteType, setDeleteType] = useState('');
  const pageSize = 10;
  const [sortField, setSortField] = useState('dataCreazione');
  const [sortDirection, setSortDirection] = useState('DESC'); // ASC o DESC

  useEffect(() => {
    contribuenteApi.get(id).then(r => setContribuente(r.data));
    fetchDocs(0);
    fetchScadenze(0);
  }, [id]);

  const fetchDocs = (page) => {
      const params = { page, size: pageSize, sort: `${sortField},${sortDirection}` };
    documentoApi.listbycontribuente(id, params)
      .then(r => {
        setDocs(r.data.content || []);
        setDocsTotalPages(r.data.totalPages || 0);
        setDocsPage(r.data.number || 0);
      })
      .catch(err =>
      setErr(extractErrorMessage(err)));
  };

  const fetchScadenze = (page) => {
    scadenzaApi.listbycontribuente(id, { page, size: pageSize })
      .then(r => {
        setScadenze(r.data.content || []);
        setScadTotalPages(r.data.totalPages || 0);
        setScadPage(r.data.number || 0);
      })
      .catch(err =>
      setErr(extractErrorMessage(err)));
  };

  const toggleSelection = (docId) => {
    setSelectedDocumentIds(prev =>
      prev.includes(docId)
        ? prev.filter(x => x !== docId)
        : [...prev, docId]
    );
  };

  const canMerge = () => selectedDocumentIds.length >= 2;

  const handleMerge = async () => {
    setMergeError(null);
    setMerging(true);
    try {
      await documentoApi.mergeDocumenti(selectedDocumentIds);
      setSelectedDocumentIds([]);
      const r = await documentoApi.listbycontribuente(id, { page: 0, size: 50 });
      setDocs(r.data.content || []);
    } catch (e) {
                setMergeError(e.response?.data?.error || e.response?.data || 'Errore durante il merge dei documenti');
                if(
                e.response?.data?.error!="I documenti non appartengono allo stesso contribuente" &&
                e.response?.data?.error!="Tipologia dei documenti non compatibile" &&
                e.response?.data?.error!="I documento non hanno lo stesso identificativo di atto/pratica" &&
                e.response?.data?.error!="Servono almeno due documenti"){
                  setSelectedDocumentIds([]);
                  fetchDocs(page);
                }
    } finally {
      setMerging(false);
    }
  };

  const confirmDelete = (id, type) => {
    setSelectedId(id);
    setDeleteType(type);
    setOpenDelete(true);
  };

  const handleDelete = async () => {
    if (deleteType === 'doc') {
      await documentoApi.delete(selectedId);
      const r = await documentoApi.listbycontribuente(id, { page: 0, size: 50 });
      setDocs(r.data.content || []);
    }
    if (deleteType === 'scad') {
      await scadenzaApi.delete(selectedId);
      const r = await scadenzaApi.listbycontribuente(id, { page: 0, size: 50 });
      setScadenze(r.data.content || []);
    }
    setOpenDelete(false);
  };

  if (!contribuente) return null;

  return (
    <Container sx={{ pb: 4 }}>
    <BackButtonDesktop />
      {/* HEADER */}
      <Typography variant="h5" sx={{ mb: 2 }}>
        {contribuente.nome} {contribuente.cognome}
      </Typography>

      {/* INFO CONTRIBUENTE */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography><b>Codice Fiscale:</b> {contribuente.codiceFiscale}</Typography>
          <Typography><b>Email:</b> {contribuente.email}</Typography>
          <Typography><b>Telefono:</b> {contribuente.telefono || 'N/A'}</Typography>

          <Stack spacing={1} sx={{ mt: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate(`/contribuenti/${id}/documenti/upload`)}
            >
              Upload PDF
            </Button>

            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate(`/contribuente/${id}/fascicolo`)}
            >
              Fascicolo
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* MERGE */}
      <ProtectedAction user={user} contractState={user.statoContratto}>
        <Button
          fullWidth
          variant="contained"
          color="warning"
          disabled={!canMerge() || merging}
          onClick={handleMerge}
        >
          Unisci documenti selezionati
        </Button>
      </ProtectedAction>

      {mergeError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {mergeError}
        </Alert>
      )}

      {/* DOCUMENTI */}
      <Typography variant="h6" sx={{ mt: 3 }}>
        Documenti
      </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              {Array.from({ length: docsTotalPages }, (_, i) => (
                <Button
                  key={i}
                  variant={i === docsPage ? "contained" : "outlined"}
                  onClick={() => fetchDocs(i)}
                >
                  {i + 1}
                </Button>
              ))}
            </Stack>
      {docs.map(d => (
        <Accordion key={d.id}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title="seleziona per merge">
              <Checkbox
                checked={selectedDocumentIds.includes(d.id)}
                onChange={() => toggleSelection(d.id)}
              />
              </Tooltip>
              <Typography>
                {d.tipo} - {d.numeroDocumento}
              </Typography>
              {d.rateSaltate > 0 && (
                <Badge color="error" badgeContent={d.rateSaltate} />
              )}
            </Stack>
          </AccordionSummary>

          <AccordionDetails>
            <Typography>{d.descrizione}</Typography>
            <Typography variant="body2">
              Creato il: {d.dataCreazione}
            </Typography>

            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <IconButton onClick={() => navigate(`/documenti/${d.id}`)}>
                <Visibility />
              </IconButton>
              <IconButton onClick={() => navigate(`/documenti/edit/${d.id}`)}>
                <Edit />
              </IconButton>
              <IconButton color="error" onClick={() => confirmDelete(d.id, 'doc')}>
                <Delete />
              </IconButton>
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              {Array.from({ length: docsTotalPages }, (_, i) => (
                <Button
                  key={i}
                  variant={i === docsPage ? "contained" : "outlined"}
                  onClick={() => fetchDocs(i)}
                >
                  {i + 1}
                </Button>
              ))}
            </Stack>
      {/* SCADENZE */}
      <Typography variant="h6" sx={{ mt: 3 }}>
        Scadenze
      </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          {Array.from({ length: scadTotalPages }, (_, i) => (
            <Button
              key={i}
              variant={i === scadPage ? "contained" : "outlined"}
              onClick={() => fetchScadenze(i)}
            >
              {i + 1}
            </Button>
          ))}
        </Stack>
      {scadenze.map(s => (
        <Accordion key={s.id}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>
              {s.tipo} – {s.dataScadenza}
            </Typography>
          </AccordionSummary>

          <AccordionDetails>
            <Typography>Importo: {s.importo}</Typography>
            <Typography>Stato: {s.stato}</Typography>

            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <IconButton onClick={() => navigate(`/scadenze/${s.id}`)}>
                <Visibility />
              </IconButton>
              <IconButton onClick={() => navigate(`/scadenze/edit/${s.id}`)}>
                <Edit />
              </IconButton>
              <IconButton color="error" onClick={() => confirmDelete(s.id, 'scad')}>
                <Delete />
              </IconButton>
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          {Array.from({ length: scadTotalPages }, (_, i) => (
            <Button
              key={i}
              variant={i === scadPage ? "contained" : "outlined"}
              onClick={() => fetchScadenze(i)}
            >
              {i + 1}
            </Button>
          ))}
        </Stack>
      <ConfirmDialog
        open={openDelete}
        onCancel={() => setOpenDelete(false)}
        onConfirm={handleDelete}
        title="Conferma eliminazione"
        message="Sei sicuro di voler eliminare questo elemento?"
      />
    </Container>
  );
}
